import { useState } from "react"

// MUI
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import IconButton from "@mui/material/IconButton"
import List from "@mui/material/List"
import CircularProgress from "@mui/material/CircularProgress"
import Alert, { AlertColor } from "@mui/material/Alert"
import Snackbar from "@mui/material/Snackbar"
import Switch from "@mui/material/Switch"
import FormControlLabel from "@mui/material/FormControlLabel"
import Stack from "@mui/material/Stack"
import Chip from "@mui/material/Chip"

// Icons
import SearchIcon from '@mui/icons-material/Search'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import RunIcon from '@mui/icons-material/Start'
import ViewResultsIcon from '@mui/icons-material/ListAlt'


import TestIcon from '@mui/icons-material/GraphicEq'
import ClearIcon from '@mui/icons-material/Clear'

// Custom 
import { PairListItem } from "./PairListItem"
import { Indicators } from "../../Indicators"
import { LottieLoading } from "../../placeholder/LottieLoading"

// Redux
import { useDispatch, useSelector } from "react-redux"
import { clearSettings, setBacktestConfig, setBacktestSettings } from "../../../redux/reducers/backtesting/backtestingReducer"
import { ApplicationState } from "../../../interface/ApplicationState"
import { TakeProfitAndStopLossConfig } from "./TakeProfitAndStopLossConfig"
import { ScheduleConfig } from "./ScheduleConfig"
import { ChartIntervalConfig } from "./ChartIntervalConfig"
import { useFilter } from "./hooks/useFilter"
import { useUpdateBacktest } from "./hooks/useUpdateBacktest"
import { Transition } from "../../../animations/Transition"
import Box from "@mui/material/Box"
import { ConfirmModal } from "../Confirm/ConfirmModal"
import { BackTestApi } from "../../../api/BackTestApi"
import { BuySellLimit } from "./BuySellLimit"
import { getSymbolsBegin } from "../../../redux/actions/userActions"
import { ViewResultsByConfig } from "./ViewResultsByConfig"
import { alpha, useTheme } from "@mui/material/styles"
import { styles as createStyles } from "./CreateCustomBackTest"

interface EditCustomBacktestProps
{
  open: boolean
  onClose: () => void
}

export const EditCustomBacktest = ({ open, onClose }: EditCustomBacktestProps) => {
  const theme = useTheme()


  const { handleUpdate, updating, updateResult, setUpdateResult } = useUpdateBacktest()
  const [ deleting, setDeleting ] = useState(false)
  const [ deleteResult, setDeleteResult ] = useState<{ message: string, color: AlertColor} | undefined>(undefined)
  const [ query, setQuery ] = useState("")
  const [ showConfirmDelete, setShowConfirmDelete ] = useState(false)
  const symbols = useSelector((state: ApplicationState) => state.userStore.symbols)
  const isGettingSymbols = useSelector((state: ApplicationState) => state.userStore.isGettingSymbols)
  const [ showResults, setShowResults ] = useState(false)

  const dispatch = useDispatch()
  const config = useSelector((state: ApplicationState) => state.backtesting.config)
  const settings = useSelector((state: ApplicationState) => state.backtesting)
  const filteredSymbols = useFilter(query, symbols)

  const handleClearSearch = () => setQuery("")
  
  const handleSelectPair = (pair: string) => {
    setQuery("")
    dispatch(setBacktestConfig({ tradingPair: pair }))
    dispatch(setBacktestSettings({ isEnabled: true }))
  } 

  const handleEditPair = async () => {
    if(!symbols || symbols.length === 0) dispatch(getSymbolsBegin())
    dispatch(setBacktestConfig({ tradingPair: ""}))
  }

  const handleClose = () => {
    dispatch(clearSettings())
    onClose()
  }

  const handleDelete = async () => {

    setDeleting(true)

    try
    {
      if(!settings._id) return
      setShowConfirmDelete(false)
      const response = await new BackTestApi().deleteCustomTest(settings._id)
      if(response)
      {
        setDeleteResult({ message: "Deleted test OK", color: "success" })
        setTimeout(() => handleClose(), 3000)
      }
     
    }
    catch(error)
    {
      const errorCast = error as any
      setDeleteResult({ message: errorCast?.message?.response?.data?.error || "Looks like something went wrong while deleting the custom backtest", color: "error" })
    }

    setDeleting(false)
  }

  const canSave = () => {
    if(!settings.accountBalanceUsdt || !settings.friendlyName || !settings.schedule || !settings.config?.tradingPair || !settings.config.chartInterval) return false
    return true
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: "18px",
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(180deg, ${alpha("#101c2d", 0.98)} 0%, ${alpha("#0a1320", 0.99)} 100%)`
              : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#eef5ff", 0.98)} 100%)`,
          border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 24px 60px ${alpha("#01050d", 0.5)}`
              : `0 24px 60px ${alpha("#9db3d1", 0.22)}`,
        }
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 3, pb: 2, borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}` }}>
        <Grid container direction="row" spacing={2} alignItems="center"> 
          <Grid item>
            <Box sx={createStyles.headerIcon(theme)}>
              <TestIcon />
            </Box>
          </Grid>
          <Grid item flex={1}>
            <Typography variant="overline" sx={{ letterSpacing: 1.5, color: alpha(theme.palette.text.primary, 0.64) }}>
              Backtesting Setup
            </Typography>
            <Typography variant="h5">Edit Custom Backtest</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: alpha(theme.palette.text.primary, 0.68) }}>
              Update the saved config, run it immediately, inspect prior results, or remove it entirely.
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleClose} sx={createStyles.closeButton(theme)}><CloseIcon /></IconButton>
          </Grid>
        </Grid>
        </DialogTitle>

      { isGettingSymbols && <LottieLoading animation={require("../../../animations/backtesting-lottie.json")} height={150} width={150} loop text="Getting Trading Pairs" containerHeight="20vh"/>}

     { !isGettingSymbols && <DialogContent sx={{ px: 3, py: 2.5 }}>

        <Grid container direction="column" spacing={2}>

          { !config?.tradingPair && <>
            <Grid item xs={12}>
              <Box sx={createStyles.sectionCard(theme)}>
              <Typography variant="overline" sx={createStyles.sectionLabel(theme)}>Change Pair</Typography>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Search for a market</Typography>
              <TextField 
                 value={query}
                 onChange={(e: any) => setQuery(e.target.value)}
                 InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1}} />,
                  endAdornment: <IconButton onClick={handleClearSearch}><ClearIcon /></IconButton>
                 }}
                 autoFocus 
                 fullWidth 
                 placeholder="Search for a trading pair to begin" />
                { query && <Typography variant="caption">{`Found ${filteredSymbols.length} trading pairs`}</Typography> }
                </Box>
            </Grid>

            <Grid item>
              <Box sx={createStyles.sectionCard(theme)}>
              <List style={styles.list}>
                { filteredSymbols.map((item, key) => <PairListItem key={key} item={item} onClick={handleSelectPair} />)}
              </List>
              </Box>
            </Grid></> }


            { config?.tradingPair && <>

            <Grid item>
              <Box sx={createStyles.heroCard(theme)}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="overline" sx={createStyles.sectionLabel(theme)}>Backtest Summary</Typography>
                    <Typography variant="h5" sx={{ mb: 1.5 }}>Edit the saved config and confirm the setup</Typography>
                    <TextField 
                      fullWidth 
                      placeholder="Custom backtest name - E.G My first backtest" 
                      value={settings.friendlyName} 
                      onChange={(e: any) => dispatch(setBacktestSettings({ friendlyName: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack spacing={1.25} alignItems={{ xs: "flex-start", md: "stretch" }}>
                      <Chip label={config?.tradingPair} sx={createStyles.summaryChip(theme)} />
                      <Chip label={config?.chartInterval ? `Interval ${config.chartInterval}` : "Interval not set"} sx={createStyles.summaryChip(theme)} />
                      <Chip label={settings?.schedule ? `Schedule ${settings.schedule}h` : "Schedule not set"} sx={createStyles.summaryChip(theme)} />
                      <Chip label={settings?.isEnabled ? "Schedule active" : "Schedule paused"} sx={createStyles.summaryChip(theme)} />
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          
            <Grid item>
              <Box sx={createStyles.sectionCard(theme)}>
                <Typography variant="overline" sx={createStyles.sectionLabel(theme)}>Selected Pair</Typography>
                <PairListItem onEdit={handleEditPair} isSelected onClick={() => {}} item={{ symbol: config?.tradingPair, baseAsset: "", quoteAsset: "", status: "Select Pair" } } />
              </Box>
            </Grid>

            <Grid item container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={createStyles.sectionCard(theme)}>
                  <Typography variant="overline" sx={createStyles.sectionLabel(theme)}>Schedule State</Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>Test Enabled</Typography>
                  <FormControlLabel checked={settings?.isEnabled} label="Enable or disable this scheduled test" onChange={(_, value) => dispatch(setBacktestSettings({ isEnabled: value }))} control={<Switch />} />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={createStyles.sectionCard(theme)}>
                  <Typography variant="overline" sx={createStyles.sectionLabel(theme)}>Notifications</Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>Notify Config</Typography>
                  <FormControlLabel checked={settings?.notifyAfterComplete} label="Send push message after test is complete" onChange={(_, value) => dispatch(setBacktestSettings({ notifyAfterComplete: value }))} control={<Switch />} />
                </Box>
              </Grid>
            </Grid>

            <Grid item>
              <Box sx={createStyles.sectionCard(theme)}>
                <BuySellLimit />
              </Box>
            </Grid>

            <Grid item container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={createStyles.sectionCard(theme)}>
                  <ChartIntervalConfig />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={createStyles.sectionCard(theme)}>
                  <ScheduleConfig />
                </Box>
              </Grid>
            </Grid>
          
            <Grid item>
              <Box sx={createStyles.sectionCard(theme)}>
                <TakeProfitAndStopLossConfig />
              </Box>
            </Grid>

            <Grid item>
              <Box sx={createStyles.sectionCard(theme)}>
                <Indicators isBacktest />
              </Box>
            </Grid>
        
            </>}

        </Grid>

      </DialogContent> }

      <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`, backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.14 : 0.02) }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ xs: "stretch", lg: "center" }} sx={{ width: "100%" }}>
          <Button variant="outlined" color="secondary" onClick={() => setShowConfirmDelete(true)} startIcon={<DeleteIcon />} sx={styles.secondaryAction(theme)}>Delete</Button>
          <Box flex={1} />
          <Button onClick={() => setShowResults(true)} variant="outlined" color="warning" startIcon={<ViewResultsIcon />} sx={styles.secondaryAction(theme)}>View Results</Button>
          <Button variant="outlined" onClick={() => handleUpdate({ ...settings, nextRun: new Date().toISOString() })} disabled={updating || !canSave()} startIcon={updating ? <CircularProgress size={16} /> : <RunIcon />} sx={styles.secondaryAction(theme)}>{ updating ? "Scheduling..." : "Run now" }</Button>
          <Button variant="contained" onClick={() => handleUpdate(settings)} disabled={updating || !canSave()} startIcon={updating ? <CircularProgress size={16} /> : <SaveIcon />} sx={styles.primaryAction}>{ updating ? "Updating" : "Update Backtest" }</Button>
        </Stack>
      </DialogActions>

      { updateResult && <Snackbar open autoHideDuration={3000} onClose={() => setUpdateResult(undefined)} >
        <Alert variant="filled" severity={updateResult.color}>{updateResult?.message}</Alert>
      </Snackbar> }

      { deleting && <Snackbar open onClose={() => {}} >
        <Alert variant="filled" severity="info">Deleting...</Alert>
      </Snackbar> }

      { deleteResult && <Snackbar open autoHideDuration={3000} onClose={() => setDeleteResult(undefined)} >
        <Alert variant="filled" severity={deleteResult.color}>{deleteResult?.message}</Alert>
      </Snackbar> }

      <ConfirmModal 
      id=""
      title="Confirm delete custom backtest"
      description="Are you sure you want to delete this backtest?"
      open={showConfirmDelete}
      onConfirm={handleDelete}
      handleClose={() => setShowConfirmDelete(false)}
      />

      { showResults && <ViewResultsByConfig onClose={() => setShowResults(false)} open />}

    </Dialog>
  )
}

const styles = {
  list: {
    overflow: "auto" as "auto",
    height: 300
  },
  secondaryAction: (theme: any) => ({
    minHeight: 44,
    borderRadius: "12px",
    borderColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.12 : 0.5),
  }),
  primaryAction: {
    minHeight: 44,
    minWidth: 180,
    borderRadius: "12px",
    boxShadow: "none",
  },
}
